import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ApplicationState, ThunkApi } from ".";
import storeCache from "@/models/storeCache";

import { gaTrackingId } from "@/lib/config";

const sliceName = "navSlice";

// -----------------
// STATE - This defines the type of data maintained in this Redux slice.
export interface NavState extends NavPageState {
	gaInitialized: boolean;
}

const initialState: NavState = {
	gaInitialized: false,
	pageName: "",
	isHidden: false,
};

// -----------------
// Thunk action creators
const initializeGA = createAsyncThunk<boolean, void, ThunkApi>(`${sliceName}/initializeGA`, async (_, { getState }) => {
	const navState = getState().navState;
	if (!navState.gaInitialized) {
		const GA4React = (await import("ga-4-react")).default;
		if (!GA4React.isInitialized()) {
			const ga4react = new GA4React(gaTrackingId);
			storeCache.ga4 = await ga4react.initialize();
		}
		return GA4React.isInitialized();
	}
	return false;
});

type NavEvent = {
	name: string;
	category: string;
	apikey: string | null;
};

// ----------------
// Redux slice
interface NavPageState {
	pageName: string;
	isHidden: boolean;
}

const slice = createSlice({
	name: sliceName,
	initialState,
	reducers: {
		setNavPage: {
			reducer: (state, { payload }: PayloadAction<NavPageState>) => {
				if (state.gaInitialized) {
					storeCache.ga4?.pageview(payload.pageName);
				}

				state.pageName = payload.pageName;
				state.isHidden = payload.isHidden;
			},
			prepare: (pageName: string, isHidden?: boolean) => {
				isHidden = isHidden ?? false;

				return {
					payload: {
						pageName,
						isHidden,
					},
				};
			},
		},
		event: (state, { payload }: PayloadAction<NavEvent>) => {
			if (state.gaInitialized) {
				const apikey = payload.apikey ?? "";
				// guidance notes: https://developers.google.com/analytics/devguides/collection/analyticsjs/events
				// function syntax: event name, event label, event category (the object that was interacted with)
				storeCache.ga4?.event(payload.name, apikey, payload.category);
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(initializeGA.fulfilled, (state, { payload }: PayloadAction<boolean>) => {
			state.gaInitialized = payload;
		});
	},
});

// ----------------
// exports
export const selector = (state: ApplicationState): NavState => state.navState;
export const actions = {
	...slice.actions,
	initializeGA,
};
export const reducer = slice.reducer;
